import React, { useState } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../../context/WalletContext';
import { useContracts } from '../../hooks/useContracts';
import { useUSDT } from '../../hooks/useUSDT';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errorParser';
import { fmtUSDT } from '../../utils/format';
import { PACKAGE_NAMES, CEILING_MULTIPLIER } from '../../utils/constants';
import ButtonLoader from '../common/ButtonLoader';

const PackageRow = ({
  index,
  price,
  status,
  registered,
  prevActive,
  onChanged,
  isLast
}) => {
  const { account } = useWallet();
  const { plans } = useContracts();
  const { balance, allowance, approveMax } = useUSDT();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);

  const id = index + 1;
  const name = PACKAGE_NAMES[index];
  const isActive = !!status?.isActive;
  const earned = status?.totalEarned || 0;
  const ceiling = status?.ceilingLimit || 0;
  const invested = status?.totalInvested || 0;
  const available = status?.remainingLimit || 0;
  const ceilNum = parseFloat(fmtUSDT(ceiling).replace(/,/g, ''));
  const earnNum = parseFloat(fmtUSDT(earned).replace(/,/g, ''));
  const pct = ceilNum > 0 ? Math.min(100, (earnNum / ceilNum) * 100) : 0;
  const maxedOut = ceilNum > 0 && earnNum >= ceilNum;

  const priceBN = ethers.BigNumber.from(price || 0);
  const ceilingBN = ethers.BigNumber.from(ceiling || priceBN.mul(CEILING_MULTIPLIER));

  // Lock rule: requires registration, and previous package active (except P1)
  const isLocked = !registered || (id > 1 && !prevActive);

  // Visual state
  let state = 'locked';
  if (isActive) state = maxedOut ? 'maxed' : 'active';
  else if (registered && id === 1 && !isLocked) state = 'available';
  else if (registered && id > 1 && prevActive) state = 'available';

  const ensureApproval = async () => {
    if (allowance.gte(priceBN)) return true;
    notify.info('Approval', 'Approving USDT…');
    try {
      await approveMax();
      notify.success('Approved', 'USDT approval successful');
      return true;
    } catch (e) {
      notify.error('Approval failed', parseError(e));
      return false;
    }
  };
  const ensureBalance = () => {
    if (balance.lt(priceBN)) {
      notify.error('Insufficient', `Need ${fmtUSDT(priceBN)} USDT`);
      return false;
    }
    return true;
  };

  const upgrade = async () => {
    if (!plans?.write) return notify.error('Wallet', 'Connect wallet first');
    if (!ensureBalance()) return;
    setBusy(true);
    try {
      if (!(await ensureApproval())) { setBusy(false); return; }
      const tx = await plans.write.upgradePackage(id);
      notify.info('Upgrading', 'Transaction submitted…');
      await tx.wait();
      notify.success('Upgraded', `${name} activated`);
      onChanged && onChanged();
    } catch (e) {
      notify.error('Upgrade failed', parseError(e));
    } finally {
      setBusy(false);
    }
  };

  const retopup = async () => {
    if (!plans?.write) return notify.error('Wallet', 'Connect wallet first');
    if (!ensureBalance()) return;
    setBusy(true);
    try {
      if (!(await ensureApproval())) { setBusy(false); return; }
      const tx = await plans.write.retopup(id);
      notify.info('Re-topup', 'Transaction submitted…');
      await tx.wait();
      notify.success('Re-topup', `${name} re-topped up`);
      onChanged && onChanged();
    } catch (e) {
      notify.error('Re-topup failed', parseError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`tier-row tier-${state}`}>
      {/* Timeline rail — left vertical line connecting numbered nodes */}
      <div className="tier-rail">
        <div className="tier-node">
          <span className="tier-num">{String(id).padStart(2, '0')}</span>
        </div>
        {!isLast && <div className="tier-line" />}
      </div>

      {/* Body */}
      <div className="tier-body">
        {/* Top: name + price + status */}
        <div className="tier-head">
          <div className="tier-head-left">
            <div className="tier-name">
              {name}
              <span className="tier-badge-tier">TIER {id}</span>
            </div>
            <div className="tier-price">
              <span className="tier-price-num">{fmtUSDT(priceBN)}</span>
              <span className="tier-price-ccy">USDT</span>
            </div>
          </div>

          <div className="tier-head-right">
            {state === 'active' && <span className="tier-status active">● ACTIVE</span>}
            {state === 'maxed' && <span className="tier-status maxed">★ MAXED</span>}
            {state === 'available' && <span className="tier-status available">▲ READY</span>}
            {state === 'locked' && <span className="tier-status locked">◌ LOCKED</span>}
          </div>
        </div>

        {/* Stats grid */}
        {isActive ? (
          <div className="tier-stats">
            <div className="tier-stat">
              <div className="tier-stat-lbl">Invested</div>
              <div className="tier-stat-val">{fmtUSDT(invested)}</div>
            </div>
            <div className="tier-stat">
              <div className="tier-stat-lbl">Earned (Gross)</div>
              <div className="tier-stat-val green">{fmtUSDT(earned)}</div>
            </div>
            <div className="tier-stat">
              <div className="tier-stat-lbl">Ceiling</div>
              <div className="tier-stat-val">{fmtUSDT(ceilingBN)}</div>
            </div>
            <div className="tier-stat">
              <div className="tier-stat-lbl">Available</div>
              <div className="tier-stat-val yellow">{fmtUSDT(available)}</div>
            </div>
          </div>
        ) : (
          <div className="tier-stats">
            <div className="tier-stat">
              <div className="tier-stat-lbl">Activation</div>
              <div className="tier-stat-val">{fmtUSDT(priceBN)} USDT</div>
            </div>
            <div className="tier-stat">
              <div className="tier-stat-lbl">Max Earnings</div>
              <div className="tier-stat-val yellow">{CEILING_MULTIPLIER}× = {fmtUSDT(priceBN.mul(CEILING_MULTIPLIER))}</div>
            </div>
            <div className="tier-stat">
              <div className="tier-stat-lbl">Status</div>
              <div className="tier-stat-val">
                {state === 'available' && <span style={{ color: 'var(--bnb-yellow)' }}>Ready to activate</span>}
                {state === 'locked' && <span style={{ color: 'var(--text-muted)' }}>Activate previous tier first</span>}
              </div>
            </div>
            <div className="tier-stat">
              <div className="tier-stat-lbl">Cycle</div>
              <div className="tier-stat-val">Re-topup to continue</div>
            </div>
          </div>
        )}

        {/* Ceiling progress (only for active) */}
        {isActive && (
          <div className="tier-progress">
            <div className="tier-progress-meta">
              <span>{pct.toFixed(1)}% of ceiling earned</span>
              <span>{fmtUSDT(earned)} / {fmtUSDT(ceilingBN)}</span>
            </div>
            <div className="tier-progress-bar">
              <div className="tier-progress-fill" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        {/* Action footer */}
        <div className="tier-action">
          {state === 'available' && (
            <button className="btn btn-primary" onClick={upgrade} disabled={busy || !account}>
              {busy ? <ButtonLoader label="Activating" /> : `Activate Tier ${id}`}
            </button>
          )}
          {(state === 'active' || state === 'maxed') && (
            <button className="btn btn-success" onClick={retopup} disabled={busy || !account}>
              {busy ? <ButtonLoader label="Re-topup" /> : `Re-topup +${fmtUSDT(priceBN)}`}
            </button>
          )}
          {state === 'locked' && (
            <button className="btn btn-ghost" disabled>
              {!registered ? 'Register first' : `Unlock by activating Tier ${id - 1}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackageRow;
